import useSWR from "swr";
import { useMemo } from "react";
import {
  getCleaningData,
  CleaningDataItem,
  CleaningDataResponse,
  EntityData,
  FieldData,
} from "../services/cleaning-data-service";

// Re-export FieldData for backward compatibility
export type EnrichedFieldData = FieldData;

// SWR key for the client/entity/field list and its counts. Exported so cleaning
// actions can revalidate it once they change what still needs cleaning.
export const CLEANING_DATA_KEY = "/cleaning";

export interface Client {
  id: number;
  name: string;
}

export interface UseCleaningDataReturn {
  cleaningData: CleaningDataItem[];
  clients: Client[];
  isLoading: boolean;
  error: Error | undefined;
  refetch: () => void;
  getFilteredEntities: (clientIds: number[]) => EntityData[];
  getFilteredFields: (clientIds: number[]) => EnrichedFieldData[];
}

const useCleaningData = (): UseCleaningDataReturn => {
  const { data, error, mutate } = useSWR<CleaningDataResponse>(
    CLEANING_DATA_KEY,
    getCleaningData,
    {
      // Deduplicate requests within 2 seconds (2000ms)
      dedupingInterval: 2000,
      // Disable automatic revalidation on window focus
      revalidateOnFocus: false,
      // Disable automatic revalidation on network reconnect
      revalidateOnReconnect: false,
      // Automatically refetch every 5 minutes
      refreshInterval: 300000,
      // Keep previous data when revalidating to maintain UI state
      keepPreviousData: true,
      // Retry on error with exponential backoff
      shouldRetryOnError: true,
      errorRetryCount: 3,
      errorRetryInterval: 5000,
    },
  );

  const cleaningData = useMemo(() => (data ? data.results : []), [data]);

  const clients: Client[] = useMemo(
    () =>
      cleaningData.map((item) => ({
        id: item.clientId,
        name: item.clientName,
      })),
    [cleaningData],
  );

  const getFilteredEntities = useMemo(
    () =>
      (clientIds: number[]): EntityData[] => {
        const filteredClients = cleaningData.filter((item) =>
          clientIds.includes(item.clientId),
        );

        const entities = new Map<string, EntityData>();
        filteredClients.forEach((client) => {
          client.entityData.forEach((entity) => {
            const existing = entities.get(entity.entityName);
            entities.set(entity.entityName, {
              ...(existing ?? entity),
              fieldData: [...(existing?.fieldData ?? []), ...entity.fieldData],
            });
          });
        });

        return Array.from(entities.values(), (entity) => {
          const fields = new Map<number, FieldData>();
          entity.fieldData.forEach((field) => {
            const existing = fields.get(field.id);
            fields.set(field.id, {
              ...(existing ?? field),
              count: (existing?.count ?? 0) + field.count,
            });
          });
          return { ...entity, fieldData: Array.from(fields.values()) };
        });
      },
    [cleaningData],
  );

  const getFilteredFields = useMemo(
    () =>
      (clientIds: number[]): EnrichedFieldData[] =>
        getFilteredEntities(clientIds).flatMap((entity) => entity.fieldData),
    [getFilteredEntities],
  );

  return {
    cleaningData,
    clients,
    isLoading: !error && !data,
    error,
    refetch: () => {
      mutate();
    },
    getFilteredEntities,
    getFilteredFields,
  };
};

export default useCleaningData;
